/**
 * THE GROUP RAIL — the collapsible group header the desk datasheet and the phone grouped
 * list share (phone display build, PR1). Third occurrence of the rail shape (the old
 * outfit-group-head, the boxes floor-rail, the datasheet's own), so the extraction is due
 * (CLAUDE.md: shapes extract on the third). Classes stay `.ds-rail .ds-rail-{depth}` so the
 * desk renders byte-identically; the phone list scopes its own geometry under `.gl`.
 */
import type { GroupSection, RailTone } from './engine.js';

/**
 * The class that resolves a section's colour variables (`--rail-hue`, `-text`, `-soft`): the
 * semantic tone when the axis has one, else the categorical hue, else nothing. Used by the
 * rail AND by the group wrapper, whose 3px left stripe runs the length of the group — the
 * maintainer, 4 Sep 2026: "the whole group and group section title row is connected by one
 * color". Rows keep their own state edge inside it (rule 6).
 */
export function railColourClass<Row>(section: GroupSection<Row>, tone: RailTone | undefined): string {
  if (tone !== undefined) return `ds-rail-tone-${tone}`;
  if (section.hue !== null) return `ds-rail-hue-${String(section.hue)}`;
  return '';
}

export function GroupRail<Row>({
  section,
  collapsed,
  onToggle,
  tone,
}: {
  section: GroupSection<Row>;
  collapsed: boolean;
  onToggle: (path: string) => void;
  /** Frame 56: a rail in its key's SEMANTIC tone (a stage's warn, a status's ok, an overdue
   *  bucket's alert). When absent the rail wears the section's categorical HUE (0.1.457). */
  tone?: RailTone | undefined;
}): JSX.Element {
  const colour = railColourClass(section, tone);
  const closed = collapsed;
  return (
    <button
      type="button"
      className={`ds-rail ds-rail-${String(section.depth)}${colour === '' ? '' : ` ${colour}`}`}
      aria-expanded={!closed}
      onClick={() => onToggle(section.path)}
    >
      <span className="ds-rail-chevron" aria-hidden="true">
        {/* The frame's chevron: a real stroked SVG, centred on the rail — the text
            glyphs sat thin and low (fidelity round 3, 1 Sep). */}
        {closed ? (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M5.5 3.5 L9 7 L5.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M3.5 5.5 L7 9 L10.5 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      {section.depth === 0 && <span className="ds-rail-dot" aria-hidden="true" />}
      <span className="ds-rail-name">{section.label}</span>
      <span className="ds-rail-count">{section.count}</span>
    </button>
  );
}
